import React from "react";
import { Box, Typography, Button, styled } from "@mui/material";
import { DiamondWithIcon } from "./DiamondFrame";
import { HandDrawnArrow } from "./HandDrawnArrow";
import { DashedConnector } from "./DashedConnector";
import { toPlainText } from "../../../../components/RichText";
import type { SimpleNamedItem } from "../../../types";

interface KeysToSuccessScreenProps {
  keys: SimpleNamedItem[];
  onBack: () => void;
  onNext: () => void;
  isLast?: boolean;
}

const Container = styled(Box, {
  label: "KeysToSuccessContainer",
})(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  alignItems: "center",
  padding: theme.spacing(3, 8, 6),
  maxWidth: 1280,
  width: "100%",
  margin: "0 auto",
}));

const Title = styled(Typography, {
  label: "KeysToSuccessTitle",
})(() => ({
  fontFamily: "Nunito",
  fontWeight: 700,
  fontSize: "32px",
  lineHeight: "48px",
  color: "#1c252e",
  textAlign: "center",
}));

const KeysContainer = styled(Box, {
  label: "KeysContainer",
})(() => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  position: "relative",
}));

const KeyItem = styled(Box, {
  label: "KeyItem",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const KeyLabel = styled(Typography, {
  label: "KeyLabel",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: "36px",
  color: "#1c252e",
  textAlign: "center",
  width: 346,
}));

const KeyDescription = styled(Typography, {
  label: "KeyDescription",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 400,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#1c252e",
  textAlign: "center",
  width: 346,
}));

const NavContainer = styled(Box, {
  label: "KeysToSuccessNav",
})(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
}));

const BackButton = styled(Button, {
  label: "KeysToSuccessBackButton",
})(({ theme }) => ({
  backgroundColor: "rgba(145, 158, 171, 0.2)",
  color: "rgba(145, 158, 171, 0.8)",
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  minWidth: 64,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "rgba(145, 158, 171, 0.3)",
  },
}));

const NextButton = styled(Button, {
  label: "KeysToSuccessNextButton",
})(({ theme }) => ({
  backgroundColor: "#078dee",
  color: "#ffffff",
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  minWidth: 64,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#0570c4",
  },
}));

const getDescriptionText = (key: SimpleNamedItem): string => {
  if (!key.description) return "";
  // Rich text can arrive from AppSync as either a JSON string (AWSJSON) or an
  // already-parsed object; normalize to a string before feeding toPlainText.
  const asString =
    typeof key.description === "string"
      ? key.description
      : JSON.stringify(key.description);
  return toPlainText(asString) || "";
};

const KeyCard = ({ item }: { item: SimpleNamedItem }) => {
  const description = getDescriptionText(item);
  return (
    <KeyItem>
      <DiamondWithIcon iconUrl={item.icon?.url} iconAlt={item.name} />
      <KeyLabel>{item.name}</KeyLabel>
      {description && <KeyDescription>{description}</KeyDescription>}
    </KeyItem>
  );
};

const OneKeyLayout = ({ keys }: { keys: SimpleNamedItem[] }) => (
  <KeysContainer sx={{ gap: "32px", alignItems: "flex-start" }}>
    <HandDrawnArrow
      style={{ position: "absolute", left: "20%", top: 100 }}
      rotation={33}
    />
    <Box style={{ position: "absolute", left: 400, top: 50 }}>
      <KeyCard item={keys[0]} />
    </Box>
    <HandDrawnArrow
      style={{ position: "absolute", right: "18%", top: 72 }}
      rotation={220}
    />
  </KeysContainer>
);

const TwoKeyLayout = ({ keys }: { keys: SimpleNamedItem[] }) => (
  <KeysContainer sx={{ gap: "240px", alignItems: "flex-start" }}>
    <HandDrawnArrow
      style={{ position: "absolute", left: 49, top: 0 }}
      rotation={0}
      flip
    />
    <Box style={{ position: "absolute", left: 100, top: 100 }}>
      <KeyCard item={keys[0]} />
    </Box>

    <Box
      sx={{
        position: "absolute",
        left: "50%",
        top: 130,
        transform: "translateX(-50%) scaleY(-1) ",
        pointerEvents: "none",
      }}
    >
      <DashedConnector width={370} height={104} />
    </Box>

    <Box style={{ position: "absolute", left: 700, top: 100 }}>
      <KeyCard item={keys[1]} />
    </Box>
    <HandDrawnArrow
      style={{ position: "absolute", right: -10, top: 200 }}
      rotation={170}
      flip
    />
  </KeysContainer>
);

const ThreeKeyLayout = ({ keys }: { keys: SimpleNamedItem[] }) => (
  <KeysContainer
    sx={{
      gap: "24px",
      alignItems: "flex-start",
      justifyContent: "space-between",
    }}
  >
    {/* Dashed connector between cards 1 and 2 */}
    <Box
      sx={{
        position: "absolute",
        left: 299,
        top: 67,
        pointerEvents: "none",
      }}
    >
      <DashedConnector width={174} height={17} />
    </Box>

    {/* Dashed connector between cards 2 and 3 */}
    <Box
      sx={{
        position: "absolute",
        left: 703,
        top: 61,
        pointerEvents: "none",
      }}
    >
      <DashedConnector width={158} height={19} />
    </Box>

    <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
      <KeyCard item={keys[0]} />
    </Box>
    <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
      <KeyCard item={keys[1]} />
    </Box>
    <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
      <KeyCard item={keys[2]} />
    </Box>
  </KeysContainer>
);

export const KeysToSuccessScreen = ({
  keys,
  onBack,
  onNext,
  isLast,
}: KeysToSuccessScreenProps) => {
  const renderLayout = () => {
    if (keys.length === 1) return <OneKeyLayout keys={keys} />;
    if (keys.length === 2) return <TwoKeyLayout keys={keys} />;
    if (keys.length >= 3) return <ThreeKeyLayout keys={keys.slice(0, 3)} />;
    return null;
  };

  return (
    <Container>
      <Title>Keys to Success</Title>

      {renderLayout()}

      <NavContainer>
        <BackButton variant="contained" onClick={onBack}>
          Back
        </BackButton>
        <NextButton variant="contained" onClick={onNext}>
          {isLast ? "Complete" : "Next"}
        </NextButton>
      </NavContainer>
    </Container>
  );
};
